import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectTechnology, technologyStartUpdate } from "../../app/actions/technologyActions";
import { debugLogPages } from "../../app/helpers/debugHelper";
import { resolveFlowRoleContext } from "../../app/helpers/flowEngineConfigHelper";
import { attachCoordinatorPublishedEditNote, useFlowEditAfterAction } from "../../app/helpers/flowEditPageHelper";
import useCanSaveFlowEditRecord from "../../app/hooks/useCanSaveFlowEditRecord";
import { useHydrateModuleEditRecord } from "../../app/helpers/moduleEditHydrateHelper";
import { resolveFichaReturnPath } from "../../app/helpers/moduleFichaNavHelper";
import useFormValidations from "../../app/hooks/useFormValidations";
import useModuleNewEditFields from "../../app/hooks/useModuleNewEditFields";
import { attachModuleFormScope } from "../../app/helpers/moduleFormScopeHelper";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import { getSelectedProblemsOriginalCatalogsHelper, hydrateCatalogSelectFields, listToStringHelper } from "../../app/helpers/helperCatalogs";
import { concatDynamicLevelsHelper } from "../../app/helpers/helperConcat";
import useHydrateMultilevelSelects from "../../app/hooks/useHydrateMultilevelSelects";
import FlowFichaSections from "../specialist-flow-engine/components/FlowFichaSections";
import FlowEngineSection from "../specialist-flow-engine/FlowEngineSection";
import FlowEngineBandejaBanner from "../specialist-flow-engine/FlowEngineBandejaBanner";
import RecordExcelUpdateHeaderButton from "../specialist-flow-engine/components/RecordExcelUpdateHeaderButton";
import FlowEditPageTitle from "../specialist-flow-engine/components/FlowEditPageTitle";
import useFlowEditPageTitle from "../../app/hooks/useFlowEditPageTitle";

const EditTechnology = () => {
  debugLogPages(':: EditTechnology');

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /********Store*******/
  const { technologySelected } = useSelector((state) => state.technology);

  const { listFullCatalogCiius, listCatalogCiiusLevel1, listCatalogCiiusLevel2, listCatalogCiiusLevel3, listCatalogCiiusLevel4, listCatalogCiiusLevel5, listCatalogCiiusLevel6 } =
    useSelector((state) => state.catalogCiiu);

  const { listFullCatalogSections, listCatalogSectionsLevel1, listCatalogSectionsLevel2, listCatalogSectionsLevel3 } =
    useSelector((state) => state.catalogSection);

  const { listFullCatalogPhases, listCatalogPhasesLevel1, listCatalogPhasesLevel2, listCatalogPhasesLevel3, listCatalogPhasesLevel4 } =
    useSelector((state) => state.catalogPhase);

  const { listContinents, listRegions, listCountries, objectCountrySelectedInitial, objectRegionSelectedInitial, objectContinentSelectedInitial } = useSelector((state) => state.locationCountry);

  const { validationFields, arrayListPrincipal, arrayListSecondary, visibilityFlag } = useModuleNewEditFields("technology", {
    record: technologySelected,
    process: "technology"
  });

  const userRole = useSelector((state) => state.userRole);
  const auth = useSelector((state) => state.auth);
  const flowEngine = useSelector((state) => state.flowEngine);

  const { listSeries, listSophistications, listMagnitudes, listSizes, listTypologies, listGeneralAdaptations, listStructuralAdaptations, listSpecificAdaptations, listProblems, listMultimedias, listPriceRanges,
    listPermissionPublish } = useSelector((state) => state.catalog);

  /********UseForm*******/
  const { values, setValues, errors, handleChange, handleChangeMulti, handleSubmit,
    handleChangeCatalogCiiu, handleChangeCatalogSection, handleChangeCatalogPhase, handleChangeLocation, handleChangeLocationRender } = useFormValidations(
      () => TechnologyCallback(),
      technologySelected,
      validationFields
    );

  useHydrateModuleEditRecord({
    moduleProcess: "technology",
    selectedRecord: technologySelected,
    setValues,
    selectAction: selectTechnology,
    catalogs: {
      listProblems,
      listGeneralAdaptations,
      listStructuralAdaptations,
      listSpecificAdaptations
    }
  });

  /********useState y useEffect*******/
  const [isDisabledProcessButton ] = useState(false);

  useHydrateMultilevelSelects({
    values,
    setValues,
    listFullCatalogCiius,
    listFullCatalogSections,
    listFullCatalogPhases
  });


  useEffect(() => {
    // 1. Guard de Validación Temprana: Verifica que exista la función handleChange y el objeto values
    if (typeof handleChange !== 'function' || !values) return;

    // 2. Definición de la estructura de catálogos y sus respectivas listas
    const catalogMappings = [
      { id: values.catalogserie_id, list: listSeries, field: "catalogserie_id" },
      { id: values.catalogsophistication_id, list: listSophistications, field: "catalogsophistication_id" },
      { id: values.catalogsize_id, list: listSizes, field: "catalogsize_id" },
      { id: values.catalogtypology_id, list: listTypologies, field: "catalogtypology_id" },
      { id: values.catalogmagnitude_id, list: listMagnitudes, field: "catalogmagnitude_id" },
      { id: values.catalogproblem_id, list: listProblems, field: "catalogproblem_id" },
      { id: values.catalogpricerange_id, list: listPriceRanges, field: "catalogpricerange_id" },
      { id: values.catalogpermissionpublish_id, list: listPermissionPublish, field: "catalogpermissionpublish_id" }
    ];

    hydrateCatalogSelectFields(handleChange, catalogMappings);

    // 4. Dependencias optimizadas: Escucha los catálogos maestros y los IDs específicos del formulario
  }, [
    listSeries,
    listSophistications,
    listSizes,
    listTypologies,
    listMagnitudes,
    listProblems,
    listPriceRanges,
    listPermissionPublish,
    values?.catalogserie_id,
    values?.catalogsophistication_id,
    values?.catalogsize_id,
    values?.catalogtypology_id,
    values?.catalogmagnitude_id,
    values?.catalogproblem_id,
    values?.catalogpricerange_id,
    values?.catalogpermissionpublish_id
  ]);



  useEffect(() => {
    // 1. Guard de Ejecución: Verifica que la función externa exista antes de continuar
    if (typeof handleChangeLocationRender !== 'function') return;

    // 2. Definición estructurada de las locaciones iniciales
    const locations = [
      { object: objectContinentSelectedInitial, field: "catalogcontinents_id" },
      { object: objectRegionSelectedInitial, field: "catalogregions_id" },
      { object: objectCountrySelectedInitial, field: "catalogcountries_id" }
    ];

    // 3. Procesamiento directo sin bucles indexados artificiales
    locations.forEach(({ object, field }) => {
      // Guard de Propiedades: Valida existencia, tipos correctos y IDs válidos mayores a cero
      if (
        object &&
        object.id !== undefined &&
        object.id !== "undefined" && // Protección por si el backend envía el string "undefined"
        Number(object.id) > 0
      ) {
        handleChangeLocationRender(object, field);
      }
    });

    // 4. Dependencias limpias y específicas
  }, [objectContinentSelectedInitial, objectRegionSelectedInitial, objectCountrySelectedInitial]);


  const TechnologyCallback = async () => {
    debugLogPages(':: EditTechnology, TechnologyCallback, values', { values });

    let valuesSubmit = { ...values };

    valuesSubmit.general_adaptation = valuesSubmit.general_adaptation_string_list;
    valuesSubmit.structural_adaptation = valuesSubmit.structural_adaptation_string_list;
    valuesSubmit.specific_adaptation = valuesSubmit.specific_adaptation_string_list;

    valuesSubmit.catalogproblem_o = listToStringHelper(valuesSubmit.catalogproblem_list);
    valuesSubmit.catalogproblem_ids = listToStringHelper(valuesSubmit.catalogproblem_list, ";", "id");

    // Para los niveles CIIU (Mínimo 1, Máximo 6)
    valuesSubmit.catalogciiu_levels = concatDynamicLevelsHelper(values, "catalogciiu", 6);
    // Para los niveles de Sección (Mínimo 1, Máximo 3)
    valuesSubmit.catalogsection_levels = concatDynamicLevelsHelper(values, "catalogsection", 3);
    // Para los niveles de Fase (Mínimo 1, Máximo 5)
    valuesSubmit.catalogphase_levels = concatDynamicLevelsHelper(values, "catalogphase", 5);

    valuesSubmit.deleted = false;

    valuesSubmit.general_adaptation = listToStringHelper(valuesSubmit.general_adaptation_list);
    valuesSubmit.structural_adaptation = listToStringHelper(valuesSubmit.structural_adaptation_list);
    valuesSubmit.specific_adaptation = listToStringHelper(valuesSubmit.specific_adaptation_list);

    debugLogPages(':: EditTechnology, TechnologyCallback, valuesSubmit', { valuesSubmit });
    const noteGate = await attachCoordinatorPublishedEditNote(valuesSubmit, {
      record: values,
      roleKey: roleContext.roleCode,
      flowEngine,
      t
    });
    if (!noteGate.proceed) {
      return;
    }
    dispatch(technologyStartUpdate(attachModuleFormScope(noteGate.valuesSubmit, visibilityFlag), navigate));
  };

  const getMethodData = (currentData) => {
    // 1. Diccionario que centraliza los datos y niveles por clave
    const dataMapping = {
      series: { comboData: listSeries, level: 1 },
      ciius_level1: { comboData: listCatalogCiiusLevel1, level: 1 },
      ciius_level2: { comboData: listCatalogCiiusLevel2, level: 2 },
      ciius_level3: { comboData: listCatalogCiiusLevel3, level: 3 },
      ciius_level4: { comboData: listCatalogCiiusLevel4, level: 4 },
      ciius_level5: { comboData: listCatalogCiiusLevel5, level: 5 },
      ciius_level6: { comboData: listCatalogCiiusLevel6, level: 6 },
      sections_level1: { comboData: listCatalogSectionsLevel1, level: 1 },
      sections_level2: { comboData: listCatalogSectionsLevel2, level: 2 },
      sections_level3: { comboData: listCatalogSectionsLevel3, level: 3 },
      phases_level1: { comboData: listCatalogPhasesLevel1, level: 1 },
      phases_level2: { comboData: listCatalogPhasesLevel2, level: 2 },
      phases_level3: { comboData: listCatalogPhasesLevel3, level: 3 },
      phases_level4: { comboData: listCatalogPhasesLevel4, level: 4 },
      phases_level5: { comboData: listCatalogPhasesLevel4, level: 5 }, // CORREGIDO: apuntaba a Level4
      sophistications: { comboData: listSophistications, level: 1 },
      magnitudes: { comboData: listMagnitudes, level: 1 },
      sizes: { comboData: listSizes, level: 1 },
      typologies: { comboData: listTypologies, level: 1 },
      problems: { comboData: listProblems, level: 1 },
      multimedias: { comboData: listMultimedias, level: 1 },
      continents: { comboData: listContinents, level: 1 },
      regions: { comboData: listRegions, level: 1 },
      countries: { comboData: listCountries, level: 1 },
      prices: { comboData: listPriceRanges, level: 1 },
      permission_publish: { comboData: listPermissionPublish, level: 1 },
      general_adaptation: { comboData: listGeneralAdaptations, level: 1 },
      structural_adaptation: { comboData: listStructuralAdaptations, level: 1 },
      specific_adaptation: { comboData: listSpecificAdaptations, level: 1 },
    };

    // 2. Retorna los datos si la clave existe, de lo contrario devuelve valores por defecto
    return dataMapping[currentData] || { comboData: [], level: 0 };
  };

  console.log(':: EditTechnology', { 'values': values });
  console.log(':: EditTechnology', { 'errors': errors });


  debugLogPages(':: EditTechnology', { 'values': values });
  debugLogPages(':: EditTechnology', { 'errors': errors });

  //console.log("listSeries",listSeries);

  // 1. Definimos las llaves relacionales específicas para la Edición de Tecnologías
  const technologyRelations = {
    problems: "ModuleProblem_To_Technology",    // Clave de problemas para tecnología
    multimedia: "ModuleFile_To_Technology"     // Clave de archivos para tecnología
  };

  // 2. Agrupamos los props comunes para inyectarlos limpiamente en el motor dinámico
  const sharedFormProps = {
    values,
    errors,
    setValues,
    handleChange,
    handleChangeLocation,
    handleChangeMulti,
    handleChangeCatalogPhase,
    handleChangeCatalogCiiu,
    handleChangeCatalogSection,
    getMethodData,
    listProblems,
    listMultimedias,
    getSelectedProblemsOriginalCatalogs: getSelectedProblemsOriginalCatalogsHelper, // Tu helper específico de edición
    relationKeys: technologyRelations,   // Llaves de tecnología configuradas arriba
    isEditMode: true                      // IMPORTANTÍSIMO: true habilita las listas de adaptación temporal
  };

  const handleFlowAfterAction = useFlowEditAfterAction({
    moduleProcess: "technology",
    recordId: values?.id,
    dispatch,
    setValues,
    selectAction: selectTechnology
  });

  const roleContext = useMemo(
    () => resolveFlowRoleContext({ userRole, authUser: auth?.user }),
    [userRole, auth]
  );

  const canSaveRecord = useCanSaveFlowEditRecord(values, "technology");
  const isSaveDisabled = isDisabledProcessButton || !canSaveRecord;

  const pageTitle = useFlowEditPageTitle("technology");

  return (
    <div>
      <NavbarIconsModuleContext moduleProcess="technology" />
      <FlowEditPageTitle title={pageTitle}>
        <RecordExcelUpdateHeaderButton
          moduleProcess="technology"
          expectedName={values?.name}
          expectedId={values?.id}
          disabled={isSaveDisabled}
          onApplied={() => navigate(resolveFichaReturnPath("technology"))}
        />
      </FlowEditPageTitle>
      <hr className="line-hr" />
      <FlowEngineBandejaBanner record={values} moduleProcess="technology" />
      <FlowEngineSection
        record={values}
        moduleProcess="technology"
        moduleName={pageTitle}
        layoutMode="edit"
        compact
        showHistoryButton={false}
        onAfterAction={handleFlowAfterAction}
      />
      <div className="ajusteTamanio">
        <form onSubmit={handleSubmit} noValidate>
          <fieldset disabled={isSaveDisabled} className="border-0 p-0 m-0">
          <div className="row">
            <FlowFichaSections
              record={values}
              process="technology"
              arrayListPrincipal={arrayListPrincipal}
              arrayListSecondary={arrayListSecondary}
              sharedFormProps={sharedFormProps}
              visibilityFlag={visibilityFlag}
            />
          </div>

          {!isSaveDisabled && (
          <div className="btnGuardar">
            <input 
              type="submit" 
              value={t("buttons.submit")} 
              className="btn btn-primary" 
            />
          </div>
          )}
          </fieldset>
          <FlowEngineSection
            record={values}
            moduleProcess="technology"
            moduleName={pageTitle}
            layoutMode="edit"
            showSummary
            showActions={false}
            onAfterAction={handleFlowAfterAction}
          />
        </form>
      </div>
    </div>
  );
};

export default EditTechnology;